import React from 'react';
import {
  Accordion,
  AccordionItem,
  Checkbox,
  InputField,
  TreePicker,
  TreePickerPropsAndEvents,
  TreePicker_onEndOfScrollEvent,
  TreePicker_onExpansionChangeEvent,
  TreePicker_onSearchChangeEvent,
} from '@inmoment/the-kitchen';
import { getLinkTo } from '../../utils/fns';
import { documentation, KitchenClass } from '../../utils/Documentation';
import styled from 'styled-components';
import { DebugServicePlayground } from '../automated-pages-utils/DebugServicePlayground/DebugServicePlayground';
import { CodeBlock } from '../../cookbook-ui/custom-components/CodeBlock/CodeBlock';
import { ObjectControl } from '../automated-pages-utils/PropChangers/PropControls/ObjectControl';
import { ErrorBoundary } from '../../utils/ErrorBoundary';

const SpacedChildren = styled.div<{ gap?: number }>`
  display: flex;
  flex-direction: column;
  gap: ${({ gap = 48 }) => gap}px;
`;

export const TreePickerDocumentation = (): JSX.Element => {
  const [onExpansionChangeCalls, setOnExpansionChangeCalls] = React.useState<TreePicker_onExpansionChangeEvent[]>([]);
  const [onSearchChangeCalls, setOnSearchChangeCalls] = React.useState<TreePicker_onSearchChangeEvent[]>([]);
  const [onEndOfScrollCalls, setOnEndOfScrollCalls] = React.useState<TreePicker_onEndOfScrollEvent[]>([]);

  const [props, setProps] = React.useState<TreePickerPropsAndEvents>({
    categories: [
      {
        id: 'Category 1',
        title: 'Category 1',
        options: [
          { id: 'Category 1 Option 1', title: 'Category 1 Option 1' },
          { id: 'Category 1 Option 2', title: 'Category 1 Option 2' },
          { id: 'Category 1 Option 3', title: 'Category 1 Option 3' },
        ],
        subcategories: [
          {
            id: 'Category 1 Subcategory 1',
            title: 'Category 1 Subcategory 1',
            options: [
              { id: 'Category 1 Subcategory 1 Option 1', title: 'Category 1 Subcategory 1 Option 1' },
              { id: 'Category 1 Subcategory 1 Option 2', title: 'Category 1 Subcategory 1 Option 2' },
              { id: 'Category 1 Subcategory 1 Option 3', title: 'Category 1 Subcategory 1 Option 3' },
            ],
          },
        ],
      },
      {
        id: 'Category 2',
        title: 'Category 2',
        options: [
          { id: 'Category 2 Option 1', title: 'Category 2 Option 1' },
          { id: 'Category 2 Option 2', title: 'Category 2 Option 2' },
          { id: 'Category 2 Option 3', title: 'Category 2 Option 3' },
        ],
        subcategories: [
          {
            id: 'Category 2 Subcategory 1',
            title: 'Category 2 Subcategory 1',
            options: [
              { id: 'Category 2 Subcategory 1 Option 1', title: 'Category 2 Subcategory 1 Option 1' },
              { id: 'Category 2 Subcategory 1 Option 2', title: 'Category 2 Subcategory 1 Option 2' },
              { id: 'Category 2 Subcategory 1 Option 3', title: 'Category 2 Subcategory 1 Option 3' },
            ],
          },
        ],
      },
    ],
    categoryIsFetchingDataMap: {
      'Category 1': {
        options: false,
        categories: false,
      },
      'Category 1 Subcategory 1': {
        options: false,
        categories: true,
      },
      'Category 2': {
        options: true,
        categories: true,
      },
      'Category 2 Subcategory 1': {
        options: true,
        categories: false,
      },
    },
    endOfScrollOffset: 10,
    isFetchingData: true,
    onExpansionChange: (e) => setOnExpansionChangeCalls((prev) => [e, ...prev]),
    onSearchChange: (e) => setOnSearchChangeCalls((prev) => [e, ...prev]),
    onEndOfScroll: (e) => setOnEndOfScrollCalls((prev) => [e, ...prev]),
  });
  const [lazyLoadingTreePickerWrapperElement, setLazyLoadingTreePickerWrapperElement] = React.useState<HTMLDivElement | null>(null);

  const propsDocumentation = documentation.byName['TreePickerProps'] as KitchenClass;
  const getProp = (propName: string) => propsDocumentation.properties?.find(({ name }) => name === propName);

  const eventsDocumentation = documentation.byName['TreePickerEvents'] as KitchenClass;
  const getEvent = (eventName: string) => eventsDocumentation.properties?.find(({ name }) => name === eventName);

  return (
    <SpacedChildren gap={12}>
      <p className="kxi-text-600 kxi-text-normal">Additional TreePicker Documentation</p>

      <Accordion>
        <AccordionItem title="Lazy loading documentation" open>
          <ErrorBoundary>
            <SpacedChildren>
              <SpacedChildren gap={12}>
                <p>The TreePicker has several props and events that must be used to effectively implement lazy loading.</p>
                <p>
                  The "Lazy Loading Props" accordion below contains information for each prop along with a control that allows the value to be
                  changed. The values of each prop are applied to the TreePicker below the accordion.
                </p>
                <p>
                  The "Lazy Loading Events" accordion contains information for each event along with a list of all times that the event has been
                  emitted by the TreePicker below.
                </p>
                <p>
                  For an example implementation using the {getLinkTo('HierarchyPicker')}, see{' '}
                  <a href="https://git.inmoment.com/projects/GRG/repos/the-kitchen/browse/apps/sample/angular/general-industries/src/app/lazy-load/lazy-load.component.ts">
                    LazyLoadComponent
                  </a>{' '}
                  in the Kitchen repo (see <a href="https://confluence.inmoment.com/x/YQANBg">Confluence</a> for instructions on running the Kitchen's
                  "General Industries" environment locally).
                </p>
              </SpacedChildren>
              <div>
                <p>Lazy Loading Props</p>
                <Accordion>
                  <AccordionItem title="categoryIsFetchingDataMap">
                    <SpacedChildren gap={12}>
                      <p>
                        <b>Type</b>: {getLinkTo(getProp('categoryIsFetchingDataMap')?.type ?? '')}
                      </p>
                      <p>
                        {getLinkTo(getProp('categoryIsFetchingDataMap')?.description ?? '')}
                        <b>Description</b>: An object that maps a category's id to an object indicating whether that category is fetching
                        subcategories and/or options.
                      </p>
                      <div>
                        <b>Value</b>:
                        <div>
                          <ObjectControl
                            value={props.categoryIsFetchingDataMap!}
                            onChange={(categoryIsFetchingDataMap) => setProps({ ...props, categoryIsFetchingDataMap })}
                          />
                        </div>
                      </div>
                    </SpacedChildren>
                  </AccordionItem>
                  <AccordionItem title="isFetchingData">
                    <SpacedChildren gap={12}>
                      <p>
                        <b>Type</b>: {getLinkTo(getProp('isFetchingData')?.type ?? '')}
                      </p>
                      <p>
                        <b>Description</b>: {getProp('isFetchingData')?.description}
                      </p>
                      <div>
                        <b>Value</b>:{' '}
                        <div style={{ display: 'inline-block' }}>
                          <Checkbox checked={props.isFetchingData} onChange={(e) => setProps({ ...props, isFetchingData: e.target.checked })} />
                        </div>
                      </div>
                    </SpacedChildren>
                  </AccordionItem>
                  <AccordionItem title="endOfScrollOffset">
                    <SpacedChildren gap={12}>
                      <p>
                        <b>Type</b>: {getLinkTo(getProp('endOfScrollOffset')?.type ?? '')}
                      </p>
                      <p>
                        <b>Description</b>: {getProp('endOfScrollOffset')?.description}
                      </p>
                      <div>
                        <p>
                          <b>Value</b>:
                        </p>
                        <InputField
                          type="number"
                          value={`${props.endOfScrollOffset}`}
                          onChange={(e) => {
                            const asNumber = Number(e.target.value);
                            if (!isNaN(asNumber)) {
                              setProps({ ...props, endOfScrollOffset: asNumber });
                            }
                          }}
                        />
                      </div>
                    </SpacedChildren>
                  </AccordionItem>
                </Accordion>
              </div>
              <div>
                <p>Lazy Loading Events</p>
                <Accordion>
                  <AccordionItem title="onExpansionChange">
                    <p>
                      <b>Type</b>: {getLinkTo(getEvent('onExpansionChange')?.type ?? '')}
                    </p>
                    <p>
                      <b>Description</b>: {getEvent('onExpansionChange')?.description}
                    </p>
                    <div>
                      <p>
                        <b>Events</b>:
                      </p>
                      <div style={{ height: 250, overflow: 'auto' }}>
                        {onExpansionChangeCalls.length > 0 ? (
                          onExpansionChangeCalls.map((e, i) => <CodeBlock key={i} code={JSON.stringify(e, undefined, '    ')} monospace />)
                        ) : (
                          <p>No events of this type have been emitted yet. Try interacting with the component.</p>
                        )}
                      </div>
                    </div>
                  </AccordionItem>
                  <AccordionItem title="onSearchChange">
                    <p>
                      <b>Type</b>: {getLinkTo(getEvent('onSearchChange')?.type ?? '')}
                    </p>
                    <p>
                      <b>Description</b>: {getEvent('onSearchChange')?.description}
                    </p>
                    <div>
                      <p>
                        <b>Events</b>:
                      </p>
                      <div style={{ height: 250, overflow: 'auto' }}>
                        {onSearchChangeCalls.length > 0 ? (
                          onSearchChangeCalls.map((e, i) => <CodeBlock key={i} code={JSON.stringify(e, undefined, '    ')} monospace />)
                        ) : (
                          <p>No events of this type have been emitted yet. Try interacting with the component.</p>
                        )}
                      </div>
                    </div>
                  </AccordionItem>
                  <AccordionItem title="onEndOfScroll">
                    <p>
                      <b>Type</b>: {getLinkTo(getEvent('onEndOfScroll')?.type ?? '')}
                    </p>
                    <p>
                      <b>Description</b>: {getEvent('onEndOfScroll')?.description}
                    </p>
                    <div>
                      <p>
                        <b>Events</b>:
                      </p>
                      <div style={{ height: 250, overflow: 'auto' }}>
                        {onEndOfScrollCalls.length > 0 ? (
                          onEndOfScrollCalls.map((e, i) => <CodeBlock key={i} code={JSON.stringify(e, undefined, '    ')} monospace />)
                        ) : (
                          <p>No events of this type have been emitted yet. Try interacting with the component.</p>
                        )}
                      </div>
                    </div>
                  </AccordionItem>
                </Accordion>
              </div>
              <SpacedChildren gap={12}>
                <p className="kxi-text-600">TreePicker with the above props applied</p>
                <DebugServicePlayground componentWrapperElement={lazyLoadingTreePickerWrapperElement} componentName="TreePicker" />
                <div ref={setLazyLoadingTreePickerWrapperElement}>
                  <TreePicker {...props} />
                </div>
              </SpacedChildren>
            </SpacedChildren>
          </ErrorBoundary>
        </AccordionItem>
      </Accordion>
    </SpacedChildren>
  );
};
